/**
 * BetaFrictionFab — floating pencil in the bottom-right that lets Tracy
 * (or her partner) capture a friction point in ~5 seconds without leaving
 * the page they're on. URL is auto-captured. Entries flow to
 * /v3/admin/beta.
 *
 * Only renders when the backend reports `beta_mode: true`. The AppShell
 * mounts it once at the top level.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, X, Loader2 } from "lucide-react";

const SEVERITIES = ["low", "medium", "high", "blocker"];

export default function BetaFrictionFab() {
  const [enabled, setEnabled] = useState(false);
  const [open, setOpen]       = useState(false);
  const [note, setNote]       = useState("");
  const [severity, setSev]    = useState("medium");
  const [saving, setSaving]   = useState(false);
  const { pathname }          = useLocation();

  // Only render when the backend actually says we're in beta — avoids
  // clutter after Tracy re-opens the platform.
  useEffect(() => {
    let alive = true;
    api.get("/v3/beta/status")
      .then((r) => { if (alive) setEnabled(!!r.data?.beta_mode); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (!note.trim()) return;
    setSaving(true);
    try {
      await api.post("/v3/beta/journal", {
        url:      pathname,
        note:     note.trim(),
        severity,
        category: "friction",
      });
      toast.success("Logged. Nice catch.");
      setNote(""); setSev("medium"); setOpen(false);
    } catch {
      toast.error("Couldn't save — try again");
    } finally { setSaving(false); }
  }, [note, severity, pathname]);

  if (!enabled) return null;

  return (
    <>
      {/* Pencil FAB — positioned above the Jarvis chip so they don't fight */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Log a friction point"
        title="Log a friction point"
        data-testid="beta-friction-fab"
        className="fixed bottom-6 left-6 lg:left-[290px] z-[9997] h-12 w-12 rounded-full grid place-items-center text-white shadow-lg shadow-indigo-900/25 hover:shadow-xl transition-all"
        style={{ backgroundImage: "linear-gradient(135deg, #6366F1, #4F46E5)" }}
      >
        {open ? <X className="w-5 h-5" /> : <Pencil className="w-5 h-5" />}
      </button>

      {open && (
        <form
          onSubmit={submit}
          className="fixed bottom-24 left-6 lg:left-[290px] z-[9998] w-[320px] rounded-2xl border border-indigo-200 bg-white shadow-2xl shadow-slate-900/15 p-4"
          data-testid="beta-friction-panel"
        >
          <div className="text-[10px] font-black uppercase tracking-widest text-indigo-600 mb-1">
            Private beta · Friction log
          </div>
          <div className="text-[12px] text-slate-500 mb-3">
            URL: <code className="text-slate-700">{pathname}</code>
          </div>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="What broke, what confused, or what do you wish existed?"
            className="w-full text-[13px] border border-slate-200 rounded-lg p-2 focus:outline-none focus:border-indigo-400 resize-none"
            autoFocus
            data-testid="beta-friction-note"
          />
          <div className="mt-2 flex items-center gap-2">
            <div className="flex gap-1">
              {SEVERITIES.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSev(s)}
                  className={`text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded ${
                    severity === s
                      ? "bg-indigo-600 text-white"
                      : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                  }`}
                  data-testid={`beta-friction-sev-${s}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <button
              type="submit"
              disabled={saving || !note.trim()}
              className="ml-auto rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[12px] font-bold px-3 py-1.5 disabled:opacity-50"
              data-testid="beta-friction-submit"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Log it"}
            </button>
          </div>
        </form>
      )}
    </>
  );
}
